<template>
  <v-container fluid
               class="px-0 pt-0">
    <div v-if="slidesLoading"
         class="d-flex justify-center">
      <v-progress-circular indeterminate color="primary"
                           size="60">
      </v-progress-circular>
    </div>
    <template v-else>
      <v-card class="mb-4">
        <v-card-text v-if="isGenerating">
          <v-progress-linear :value="generatingStatus.progress"
                             height="36"
                             color="primary">
            <template v-slot:default="{value}">
              <strong class="white--text">
                Generating slides: {{ value }}%
              </strong>
            </template>
          </v-progress-linear>
        </v-card-text>
        <v-card-actions v-else
                        class="px-4">

          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs"
                     v-on="on">
                Actions
                <v-icon right>
                  fas fa-fw fa-chevron-down
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="generateSlides">
                <v-list-item-content>
                  <v-list-item-title>
                    <template v-if="hasSlides">
                      Regenerate slides
                    </template>
                    <template v-else>
                      Generate slides
                    </template>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="hasSlides"
                           @click="resetSlideMetadata">
                <v-list-item-content>
                  <v-list-item-title>
                    Reset slide details
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="hasSlides"
                           @click="deleteUnnecessarySlides">
                <v-list-item-content>
                  <v-list-item-title>
                    Delete unnecessary slides
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

        </v-card-actions>
      </v-card>

      <template v-if="isGenerating === false">
        <v-form @submit.prevent="updateInstructions"
                :disabled="instructionsForm.busy">
          <v-card class="mb-4">
            <v-card-text>
              <v-textarea v-model="instructionsForm.builder_instructions"
                          label="Presentation Instructions"
                          rows="3"
                          auto-grow
                          outlined
                          dense
                          clearable
                          :error-messages="instructionsForm.errors.get('builder_instructions')"
                          hint="Optional: Write instructions that will be shown in the presentation builder."
                          persistent-hint>
              </v-textarea>
              <div class="text-caption">
                You can use Markdown syntax. Read the <a href="https://www.markdownguide.org/cheat-sheet/" target="_blank">Markdown Cheat Sheet</a>.
              </div>
            </v-card-text>
            <v-card-actions class="px-4">
              <v-btn type="submit"
                     color="primary"
                     :disabled="instructionsForm.busy">
                Update Instructions
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>

        <v-tabs v-model="form.status"
                class="bt-gl br-gl bl-gl">
          <v-tab tab-value="">
            All ({{ slides.length }})
          </v-tab>
          <v-tab tab-value="Draft">
            Draft ({{ slideCount('Draft') }})
          </v-tab>
          <v-tab tab-value="Active">
            Active ({{ slideCount('Active') }})
          </v-tab>
          <v-tab tab-value="Missing">
            Missing ({{ slideCount('Missing') }})
          </v-tab>
          <v-tab tab-value="Replaced">
            Replaced ({{ slideCount('Replaced') }})
          </v-tab>
          <v-tab tab-value="Inactive">
            Inactive ({{ slideCount('Inactive') }})
          </v-tab>
        </v-tabs>

        <template v-if="filteredSlides.length > 0">
          <v-divider class="mb-4"></v-divider>
          <AdminSlideForm v-if="isGenerating === false"
                          v-for="slide in filteredSlides"
                          :key="slide.id"
                          :slide="slide"
                          :slides="slides"
                          class="mb-4"
                          @updated="fetchData">
          </AdminSlideForm>
        </template>

        <template v-else-if="form.status">
          <v-divider class="mb-4"></v-divider>
          <v-card tile
                  flat
                  class="br-gl bb-gl bl-gl">
            <v-card-text>
              There are no <strong>{{ form.status.toUpperCase() }}</strong> slides.
            </v-card-text>
          </v-card>
        </template>

        <template v-else-if="slides.length === 0">
          <v-card tile
                  flat
                  class="br-gl bb-gl bl-gl">
            <v-card-text>
              There are no slides.
            </v-card-text>
          </v-card>
        </template>

      </template>
    </template>
  </v-container>
</template>
<script>
import PageTitle from '../../components/PageTitle.vue';
import slideDeckService from '../../services/slideDeckService';
import {isInteger, orderBy} from 'lodash';
import AdminSlideForm from './components/AdminSlideForm.vue';
import Echo from '../../plugins/echo';
import DOMPurify from 'dompurify';
import {Form} from 'vform'

export default {
  components: {AdminSlideForm, PageTitle},
  props: {
    presentation: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      instructionsForm: new Form({ // MLD-7308
        builder_instructions: this.presentation.builder_instructions,
      }),
      form: new Form({
        status: '',
      }),
      generating: false,
      generatingError: null,
      slidesLoading: false,
      slidesLoaded: false,
      slides: [],
      generatingStatus: {
        generating: false,
        progress: 0,
      },
    }
  },
  computed: {
    hasSlides() {
      return this.slides.length > 0;
    },
    isGenerating() {
      return !!this.generatingStatus?.generating
    },
    filteredSlides() {
      if (this.form.status) {
        return this.slides.filter(s => s.status === this.form.status);
      }
      return this.slides;
    },
  },
  beforeRouteUpdate(to, from, next) {
    console.log('beforeRouteUpdate');
    this.stopEcho(this.presentation);
    next();
  },
  beforeRouteLeave(to, from, next) {
    console.log('beforeRouteLeave');
    this.stopEcho(this.presentation);
    next();
  },
  created() {
    this.initEcho();
    this.fetchData();
  },
  methods: {
    slideCount(status) {
      return this.slides.filter(s => s.status === status).length;
    },
    stopEcho(presentation) {
      Echo.private(presentation.channel)
        .stopListening('SlideBuilder.SlideGenerationStarted')
        .stopListening('SlideBuilder.SlideGenerationProgressEvent')
        .stopListening('SlideBuilder.SlideGenerationFinished')
        .stopListening('SlideBuilder.SlideGenerationFailed')
    },
    initEcho() {
      console.log('Echo is listening for broadcast slide events for presentation', this.presentation.id);
      Echo.private(this.presentation.channel)
        .listen('SlideBuilder.SlideGenerationStarted', e => {
          console.log('SlideGenerationStarted');
        })
        .listen('SlideBuilder.SlideGenerationProgressEvent', e => {
          console.log('SlideGenerationProgressEvent', e.progress);
          if (isInteger(e.progress)) {
            this.generatingStatus = {
              generating: true,
              progress: e.progress,
            }
          } else {
            this.generatingStatus = null;
          }
        })
        .listen('SlideBuilder.SlideGenerationFinished', e => {
          console.log('SlideGenerationFinished');
          this.$toast.success('Slides generated successfully.');
          this.fetchData();
        })
        .listen('SlideBuilder.SlideGenerationFailed', e => {
          console.warn('SlideGenerationFailed', e.error_message);
          this.$toast.error(DOMPurify.sanitize(e.error_message ?? 'There was an error generating slides.'));
          this.fetchData();
        })
    },
    fetchData() {
      this.slidesLoading = true;
      this.getGeneratingStatus();
      slideDeckService.getSlidesForPresentation(this.presentation.id)
        .then(data => {
          this.slides = orderBy(data, ['position', 'id'], ['asc', 'asc']);
        })
        .finally(() => {
          this.slidesLoaded = true;
          this.slidesLoading = false;
        });
    },
    getGeneratingStatus() {
      slideDeckService.getGeneratingStatusFor(this.presentation.id)
        .then(data => {
          this.generatingStatus = data;
        })
        .catch(err => {
          this.generatingStatus = {
            generating: false,
            progress: 0,
          };
        })
    },
    generateSlides() {
      slideDeckService.generateSlidesFor(this.presentation.id)
        .then(data => {
          this.generating = data.generating;
          this.fetchData();
        })
        .catch(err => {
          this.generating = false;
          this.generatingError = err.response.data.message;
        })
    },
    resetSlideMetadata() {
      if (confirm('This will reset the name, description, and other details to the default values for each slide. Are you sure you want to proceed?')) {
        slideDeckService.resetSlideMetadataFor(this.presentation.id)
          .then(data => {
            this.$toast.success('Slide details have been reset.');
            this.$emit('updated');
          })
          .catch(err => {

          })
      }
    },
    async deleteUnnecessarySlides() {
      if (confirm('Are you sure you want to delete unnecessary slides?')) {
        await slideDeckService.deleteUnnecessarySlides(this.presentation.id)
        this.$emit('updated');
        window.location.reload();
      }
    },
    async updateInstructions() { // MLD-7308
      if (this.instructionsForm.busy) {
        return;
      }
      try {
        await this.instructionsForm.patch(`/api/presentations/${this.presentation.id}/builder-instructions`)
        this.$toast.success('Instructions updated.');
      } catch (e) {
      }
    }
  }
}
</script>
